import { CarouselNext, CarouselPrevious } from "hec/components/ui/carousel";
import { CircleArrowLeft, CircleArrowRight } from "lucide-react";
import type * as React from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import ChevronCircleLeft from "~/hec/assets/icons/chevron-circle-left";
import ChevronCircleRight from "~/hec/assets/icons/chevron-circle-right";
import ChevronLeft from "~/hec/assets/icons/chevron-left";
import ChevronRight from "~/hec/assets/icons/chevron-right";

type SvgIcon = React.FC<React.SVGProps<SVGSVGElement>>;

/**
 * Chevrons default to a 44px target (WCAG 2.5.5) — the glyph alone is too small
 * to hit comfortably. These are presentation attributes, so an author's
 * width / height styles still win.
 */
const CHEVRON_SIZE = 44;
const withChevronSize = (Icon: SvgIcon): SvgIcon => {
  const Sized: SvgIcon = props => (
    <Icon width={CHEVRON_SIZE} height={CHEVRON_SIZE} {...props} />
  );
  Sized.displayName = `Sized(${Icon.name})`;
  return Sized;
};

const carouselIconNamesMap: Record<
  string,
  {
    previous: React.FC<React.SVGProps<SVGSVGElement>>;
    next: React.FC<React.SVGProps<SVGSVGElement>>;
  }
> = {
  "Circle Arrow": {
    previous: CircleArrowLeft as unknown as React.FC<
      React.SVGProps<SVGSVGElement>
    >,
    next: CircleArrowRight as unknown as React.FC<
      React.SVGProps<SVGSVGElement>
    >,
  },
  Chevron: {
    previous: withChevronSize(ChevronLeft),
    next: withChevronSize(ChevronRight),
  },
  "Chevron Circle": {
    previous: withChevronSize(ChevronCircleLeft),
    next: withChevronSize(ChevronCircleRight),
  },
};

// eslint-disable-next-line react-refresh/only-export-components
export const iconNames = Object.keys(carouselIconNamesMap);

const carouselButtonComponentMap = {
  previous: CarouselPrevious,
  next: CarouselNext,
} as const;

/** 75% white — the circle reads over hero imagery without hiding it. */
const DEFAULT_FILL_COLOR = "rgba(255, 255, 255, 0.75)";

interface CustomCarouselButtonProps {
  type: "previous" | "next";
  icon?: string;
  fillColor?: string;
  overlapContent?: boolean;
  attributes?: Record<string, string>;
  "data-testid"?: string;
}

export const CustomCarouselButton = (props: CustomCarouselButtonProps) => {
  const {
    type,
    icon = "Circle Arrow",
    overlapContent = false,
    fillColor = DEFAULT_FILL_COLOR,
    attributes,
    "data-testid": dataTestId,
  } = props;
  const CarouselButton = carouselButtonComponentMap[type];
  const iconMap = carouselIconNamesMap[icon];
  const buttonIcon = iconMap?.[type] as
    | React.FC<React.SVGProps<SVGSVGElement>>
    | undefined;

  return (
    <CarouselButton
      overlapContent={overlapContent}
      insetOverlap
      fillColor={fillColor}
      buttonIcon={buttonIcon}
      iconAttributes={attributes}
      data-testid={dataTestId || "carouselButton-CarouselButton"}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const CarouselButtonDefinition: RegisteredComponent = {
  component: CustomCarouselButton,
  name: "CarouselButton",
  noWrap: true,
  inputs: [
    {
      name: "type",
      type: "string",
      enum: ["previous", "next"],
      required: true,
    },
    {
      name: "icon",
      type: "string",
      enum: iconNames,
      defaultValue: "Circle Arrow",
    },
    {
      name: "fillColor",
      type: "color",
      defaultValue: DEFAULT_FILL_COLOR,
      helperText:
        "Fills the circle on Circle Arrow and Chevron Circle. Chevron has no circle to fill.",
      // The plain chevron is a filled path in the text colour — nothing to fill.
      showIf: `options.get('icon') !== 'Chevron'`,
    },
    { name: "overlapContent", type: "boolean", defaultValue: false },
  ],
};
